import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { setAuthUser } from "@/lib/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=loading, false=unauthed, obj=user
    const [error, setError] = useState("");

    const check = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // 401 is the expected unauthenticated path — surface anything else for debugging
            if (e?.response?.status && e.response.status !== 401) {
                console.warn("[auth] /me check failed", e?.message || e);
            }
            setUser(false);
        }
    }, []);

    useEffect(() => {
        check();
    }, [check]);

    // Keep the synchronous role cache (@/lib/auth) in sync with context state.
    useEffect(() => {
        setAuthUser(user && typeof user === "object" ? user : null);
    }, [user]);

    const login = useCallback(async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.requires_2fa) {
                // Don't set user yet — the caller will surface a 2FA prompt
                return { requires2FA: true, challengeToken: data.challenge_token };
            }
            setUser(data);
            return true;
        } catch (e) {
            const msg = formatApiError(e);
            setError(msg);
            return false;
        }
    }, []);

    const verify2FA = useCallback(async (challengeToken, code) => {
        setError("");
        try {
            const { data } = await api.post("/auth/2fa/challenge", { challenge_token: challengeToken, code });
            setUser(data.user);
            return true;
        } catch (e) {
            setError(formatApiError(e));
            return false;
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            // Best-effort: end any active support-access grant so the tenant
            // pivot never bleeds into the next session on this browser.
            if (user?.role === "admin") {
                try { await api.post("/admin/support-access/end"); } catch { /* noop */ }
            }
            await api.post("/auth/logout");
        } catch (e) {
            // Logout endpoint failure is non-fatal — we still clear local state
            console.warn("[auth] logout request failed", e?.message || e);
        }
        // Clear the one-time setup-check flag so the next user in this tab
        // gets the auto-redirect evaluated freshly on their first landing.
        try { sessionStorage.removeItem("vault_setup_check_done"); } catch { /* noop */ }
        setUser(false);
    }, [user?.role]);

    const hasRole = useCallback(
        (...roles) => Boolean(user) && roles.includes(user.role),
        [user],
    );

    const value = useMemo(
        () => ({ user, error, login, verify2FA, logout, hasRole, refresh: check }),
        [user, error, login, verify2FA, logout, hasRole, check],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
